import React, { useEffect } from "react";
import {
  Stack,
  Flex,
  Text,
  Icon,
  List,
  ListItem,
  Input,
  IconButton,
} from "@chakra-ui/core";
import {
  DrawerTitle,
  DrawerBody,
  DrawerFooter,
} from "@pency/components/structure/Drawer";
import Button from "@pency/components/controls/Button";
import CrossIcon from "@pency/components/icons/Cross";
import Stepper from "@pency/components/inputs/Stepper";
import Image from "@pency/components/display/Image";

import { CartItem } from "../../types";

import { useTranslation, usePrice } from "~/i18n/hooks";
import {
  getCount,
  getTotal,
  getFormattedPrice,
  getItemMaxStockInCart,
} from "~/cart/selectors";
import { getVariantsString, getVariantsPrice } from "~/product/selectors";
import { formatImageUrl } from "~/product/utils/formatImage";
import { Coupon } from "@pency/api/tenant/types";
import { ClientTenant } from "~/tenant/types";
import { useStoreOpen, useTenant } from "~/tenant/hooks";
import {BrandColors} from "~/ui/colorMode";

interface Props {
  items: CartItem[];
  onDecrease: (id: CartItem["id"]) => void;
  onIncrease: (id: CartItem["id"]) => void;
  onSubmit: (couponInfo?: {
    code: string;
    discount: number;
    subtotal: number;
  }) => Promise<void>;
  onClose: VoidFunction;
  hasNextStep: boolean;
  hasCheckout: boolean;
  checkoutValidationMessage: string;
  coupons?: Coupon[];
  tenantPlan: ClientTenant["tier"];
}

const Overview: React.FC<Props> = ({
  items,
  onIncrease,
  onDecrease,
  onSubmit,
  onClose,
  hasCheckout,
  checkoutValidationMessage,
  coupons = [],
  tenantPlan,
}) => {
  const [hasValidationMessage, setHasValidationMessage] = React.useState(false);
  const [couponCode, setCouponCode] = React.useState("");
  const [appliedCoupon, setAppliedCoupon] = React.useState<Coupon | null>(null);
  const [couponError, setCouponError] = React.useState("");
  const t = useTranslation();
  const p = usePrice();
  const { country, flags } = useTenant();
  const hideDecimals = Boolean(flags?.includes("hideDecimals"));
  const { isOpen: isStoreOpen } = useStoreOpen();
  const count = getCount(items);
  const total = getTotal(items);

  // Check if tenant has premium plan
  const isPremiumPlan =
    tenantPlan === "PLAN_PREMIUM" || tenantPlan === "PLAN_TRIAL";

  // Calculate discount (only if premium plan)
  const discount =
    isPremiumPlan && appliedCoupon
      ? appliedCoupon.type === "percentage"
        ? (total * appliedCoupon.value) / 100
        : appliedCoupon.value
      : 0;

  const finalTotal = total - discount;

  function handleNext() {
    onSubmit(
      appliedCoupon
        ? {
            code: appliedCoupon.code,
            discount: discount,
            subtotal: total,
          }
        : undefined
    );
  }

  function handleDecrease(id: CartItem["id"]) {
    onDecrease(id);
  }

  function handleIncrease(id: CartItem["id"]) {
    onIncrease(id);
  }

  function validateCoupon(code: string): {
    valid: boolean;
    coupon?: Coupon;
    error?: string;
  } {
    const coupon = coupons.find(
      (c) => c.code.toUpperCase() === code.toUpperCase()
    );

    if (!coupon) {
      return { valid: false, error: t("cart.couponInvalid") };
    }

    if (!coupon.active) {
      return { valid: false, error: t("cart.couponInactive") };
    }

    if (coupon.expirationDate && coupon.expirationDate < Date.now()) {
      return { valid: false, error: t("cart.couponExpired") };
    }

    if (coupon.minimumPurchaseAmount && total < coupon.minimumPurchaseAmount) {
      return { valid: false, error: t("cart.couponMinimumNotMet") };
    }

    return { valid: true, coupon };
  }

  function handleApplyCoupon() {
    setCouponError("");

    if (!couponCode.trim()) {
      return;
    }

    const validation = validateCoupon(couponCode.trim());

    if (validation.valid && validation.coupon) {
      setAppliedCoupon(validation.coupon);
      setCouponCode("");
    } else {
      setCouponError(validation.error || "");
    }
  }

  function handleRemoveCoupon() {
    setAppliedCoupon(null);
    setCouponError("");
  }

  function transormValidationMessage() {
    // La primera línea del mensaje es el título (stock o precios) y el resto
    // son los ítems del detalle. Así el banner sirve para ambos casos sin
    // asumir que siempre es un problema de stock.
    const messageLines: string[] = checkoutValidationMessage
      .split("\n")
      .filter((line) => line.trim().length > 0);

    const [titleLine, ...detailLines] = messageLines;

    const jsxTitle = (
      <Stack isInline key="validation-title">
        <Icon name="warning" color="grey.200" width="20px" height="20px" />
        <Text color="red">{titleLine}</Text>
      </Stack>
    );

    const jsxList = (
      <List styleType="disc" key="validation-list">
        {detailLines.map((line, index) => (
          <ListItem key={`${index}-${line}`} marginLeft="5px">
            {line}
          </ListItem>
        ))}
      </List>
    );

    // Solo cuando hubo cambios aplicados (hay detalle) sugerimos recargar para
    // ver los nuevos valores reflejados en el resto de la página.
    const jsxFooter =
      detailLines.length > 0 ? (
        <Text key="validation-footer" fontSize="sm" color="strong" marginTop="4px">
          {t("products.reloadToSeeChanges")}
        </Text>
      ) : null;

    return [jsxTitle, jsxList, jsxFooter];
  }
  useEffect(() => {
    const newValidationMessage: boolean = !!(
      checkoutValidationMessage && checkoutValidationMessage.length > 0
    );

    if (newValidationMessage) {
      setHasValidationMessage(newValidationMessage);
    }
  }, [checkoutValidationMessage]);

  return (
    <BrandColors>
      <DrawerBody>
        <CrossIcon
          background="card"
          boxShadow="md"
          cursor="pointer"
          marginTop={4}
          paddingX={4}
          paddingY={3}
          position="absolute"
          right={0}
          roundedLeft="lg"
          top={0}
          onClick={onClose}
        />
        <Stack marginTop={20} spacing={6}>
          <DrawerTitle>
            {t("cart.yourOrder")} ({count})
          </DrawerTitle>
          {hasValidationMessage && (
            <Stack bg="warningBg" padding="12px" borderRadius="10px">
              {transormValidationMessage()}
            </Stack>
          )}
          <Stack shouldWrapChildren spacing={6}>
            {items.map((item) => (
              <Flex
                key={item.id}
                alignItems="flex-start"
                justifyContent="space-between"
              >
                {item.product.images?.[0] && (
                  <Image
                    fadeIn
                    height={16}
                    marginRight={3}
                    minWidth={16}
                    rounded="md"
                    src={formatImageUrl(item.product.images[0])}
                    width={16}
                  />
                )}
                <Stack flex={1} marginRight={2} spacing={1}>
                  <Text fontWeight={500} overflowWrap="break-word">
                    {item.product.title}
                  </Text>
                  {item.variants && (
                    <Text color="muted" fontSize="sm">
                      {getVariantsString(item.variants)}
                    </Text>
                  )}
                  {item.note && (
                    <Text color="muted" fontSize="sm">
                      ({item.note})
                    </Text>
                  )}
                  <Stack isInline alignItems="center" spacing={2}>
                    <Text fontWeight={500}>
                      {getFormattedPrice(item, country, hideDecimals)}
                    </Text>
                    {item.product.type === "promotional" &&
                      !!item.product.originalPrice && (
                        <Text
                          color="subtle"
                          fontSize="sm"
                          textDecoration="line-through"
                        >
                          {p(
                            (item.product.originalPrice +
                              getVariantsPrice(item.variants)) *
                              item.count
                          )}
                        </Text>
                      )}
                  </Stack>
                </Stack>
                <Stepper
                  value={item.count}
                  max={getItemMaxStockInCart(item, items)}
                  onDecrease={() => handleDecrease(item.id)}
                  onIncrease={() => handleIncrease(item.id)}
                />
              </Flex>
            ))}
          </Stack>
        </Stack>
      </DrawerBody>
      <DrawerFooter borderTopColor="border" borderTopWidth={1} marginTop={2}>
        <Stack spacing={4} width="100%">
          {/* Coupon Input Section - Only for premium plans */}
          {isPremiumPlan && !appliedCoupon && coupons.length > 0 && (
            <Stack spacing={2}>
              <Text fontSize="sm" fontWeight={500}>
                {t("cart.couponCode")}
              </Text>
              <Flex>
                <Input
                  backgroundColor="card"
                  placeholder={t("cart.couponCode")}
                  size="md"
                  value={couponCode}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setCouponCode(e.target.value)
                  }
                  onKeyPress={(e: React.KeyboardEvent) => {
                    if (e.key === "Enter") {
                      handleApplyCoupon();
                    }
                  }}
                />
                <Button
                  marginLeft={2}
                  size="md"
                  variantColor="primary"
                  onClick={handleApplyCoupon}
                >
                  {t("cart.applyCoupon")}
                </Button>
              </Flex>
              {couponError && (
                <Text color="red.500" fontSize="sm">
                  {couponError}
                </Text>
              )}
            </Stack>
          )}

          {/* Applied Coupon Display - Only for premium plans */}
          {isPremiumPlan && appliedCoupon && (
            <Stack
              isInline
              alignItems="center"
              bg="successBg"
              padding="12px"
              borderRadius="10px"
              justifyContent="space-between"
            >
              <Flex alignItems="center">
                <Icon
                  name="check-circle"
                  color="success"
                  width="20px"
                  height="20px"
                  marginRight={2}
                />
                <Stack spacing={0}>
                  <Text color="successStrong" fontSize="sm" fontWeight={500}>
                    {t("cart.couponApplied")}: {appliedCoupon.code}
                  </Text>
                  {appliedCoupon.description && (
                    <Text color="successText" fontSize="xs">
                      {appliedCoupon.description}
                    </Text>
                  )}
                </Stack>
              </Flex>
              <IconButton
                aria-label={t("cart.removeCoupon")}
                icon="close"
                size="sm"
                variant="ghost"
                onClick={handleRemoveCoupon}
              />
            </Stack>
          )}

          {/* Total Breakdown */}
          {isPremiumPlan && appliedCoupon ? (
            <Stack spacing={2}>
              <Flex
                alignItems="center"
                fontSize="md"
                justifyContent="space-between"
              >
                <Text color="muted">{t("cart.subtotal")}</Text>
                <Text color="muted">{p(total)}</Text>
              </Flex>
              <Flex
                alignItems="center"
                fontSize="md"
                justifyContent="space-between"
              >
                <Text color="successText">{t("cart.discount")}</Text>
                <Text color="successText">-{p(discount)}</Text>
              </Flex>
              <Flex
                alignItems="center"
                fontSize="lg"
                fontWeight={500}
                justifyContent="space-between"
                borderTopWidth={1}
                borderTopColor="borderStrong"
                paddingTop={2}
              >
                <Text>{t("cart.estimatedTotal")}</Text>
                <Text>{p(finalTotal)}</Text>
              </Flex>
            </Stack>
          ) : (
            <Flex
              alignItems="center"
              fontSize="lg"
              fontWeight={500}
              justifyContent="space-between"
            >
              <Text>{t("cart.estimatedTotal")}</Text>
              <Text>{p(total)}</Text>
            </Flex>
          )}

          {hasCheckout && (
            <Button
              boxShadow="lg"
              size="lg"
              variantColor="primary"
              onClick={handleNext}
              isDisabled={!isStoreOpen}
            >
              {isStoreOpen ? t("cart.initOrder") : t("schedule.closed")}
            </Button>
          )}
        </Stack>
      </DrawerFooter>
    </BrandColors>
  );
};

export default Overview;
